import React, { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { UserMinus, Loader2, Search, ShieldCheck, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const TIER = { 1: ['Nunca comprou', 'bg-rose-100 text-rose-700'], 2: ['+90 dias sem comprar', 'bg-amber-100 text-amber-700'], 3: ['+60 dias sem comprar', 'bg-yellow-100 text-yellow-700'] };
const fmtData = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—');

/* Limpeza de follows do bot: prévia por níveis → confirmação digitada → execução em background */
const FollowCleanupPanel = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useState({ inactive_days: 60, old_days: 90, frequent_min_orders: 5 });
  const [quantity, setQuantity] = useState(1000);
  const [confirm, setConfirm] = useState('');
  const timer = useRef(null);

  const loadStatus = async () => {
    try {
      const { data } = await api.get('/admin/follows/status');
      setStatus(data);
      if (data.latest_plan && (!plan || plan.plan_id === data.latest_plan.plan_id)) setPlan(data.latest_plan);
    } catch (e) { /* token indisponível → mostra aviso */ setStatus({ following: null }); }
  };

  useEffect(() => { if (open) loadStatus(); }, [open]);

  useEffect(() => {
    clearInterval(timer.current);
    if (plan?.status === 'RUNNING' || plan?.status === 'SCANNING') timer.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/follows/plan/${plan.plan_id}`);
        setPlan(data);
        if (data.status === 'DONE') loadStatus();
        if (data.status === 'PREVIEW' && plan.status === 'SCANNING') toast.success(`Prévia pronta: ${data.counts.candidates} contas inativas encontradas.`);
        if (data.status === 'FAILED') toast.error(`Prévia falhou: ${data.error || 'erro no KAKO'}`);
      } catch (e) { /* tenta de novo */ }
    }, 3000);
    return () => clearInterval(timer.current);
  }, [plan?.plan_id, plan?.status]);

  const scan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/follows/scan', params);
      setPlan(data); setConfirm('');
      toast.info('Listando as contas que o bot segue no KAKO… leva cerca de 1 minuto.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Falha ao gerar a prévia.'); }
    setBusy(false);
  };

  const execute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/follows/execute', { plan_id: plan.plan_id, quantity: Number(quantity), confirm });
      toast.success(`Limpeza iniciada: ${data.requested} contas. Acompanhe o progresso aqui.`);
      setPlan({ ...plan, status: 'RUNNING', progress: { requested: data.requested, done: 0, ok: 0, failed: 0 } });
      setConfirm('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Falha ao iniciar a limpeza.'); }
    setBusy(false);
  };

  const c = plan?.counts;
  const pr = plan?.progress;
  const pct = pr?.requested ? Math.round((pr.done / pr.requested) * 100) : 0;

  return (
    <Card data-testid="follow-cleanup-panel">
      <CardContent className="p-3 sm:p-4">
        <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between" data-testid="follow-cleanup-toggle">
          <div className="flex items-center gap-2 text-left">
            <UserMinus className="h-4 w-4 text-rose-500" />
            <div>
              <p className="text-sm font-black text-slate-800">Limpeza de follows do bot</p>
              <p className="text-[11px] text-slate-500">Libera vagas do limite de 3.000 deixando de seguir clientes inativos (prévia + confirmação)</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {status?.following != null && <Badge variant="secondary" data-testid="follow-cleanup-following">{status.following.toLocaleString('pt-BR')} seguidos</Badge>}
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </button>

        {open && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
              {[['inactive_days', 'Inativo há (dias)'], ['old_days', 'Muito inativo há (dias)'], ['frequent_min_orders', 'Cliente frequente (compras ≥)']].map(([k, label]) => (
                <label key={k} className="text-[11px] font-bold text-slate-600">{label}
                  <Input type="number" value={params[k]} onChange={(e) => setParams({ ...params, [k]: Number(e.target.value) })} className="mt-1 h-8" data-testid={`follow-cleanup-${k}`} />
                </label>
              ))}
              <div className="flex items-end">
                <Button onClick={scan} disabled={busy || plan?.status === 'RUNNING' || plan?.status === 'SCANNING'} className="h-8 w-full bg-slate-800 text-white hover:bg-slate-700" data-testid="follow-cleanup-scan">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="mr-1 h-4 w-4" /> Gerar prévia</>}
                </Button>
              </div>
            </div>
            <p className="flex items-center gap-1 text-[11px] text-slate-500"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Revendedores, clientes frequentes e contas protegidas nunca entram na lista. Quem voltar a comprar ou falar com o bot é seguido de novo automaticamente.</p>
            <p className="text-[11px] font-bold text-amber-700">Use apenas no painel de PRODUÇÃO: a prévia cruza os seguidos do bot com os pedidos do banco deste ambiente.</p>

            {plan?.status === 'SCANNING' && (
              <p className="flex items-center gap-2 text-xs font-bold text-slate-600" data-testid="follow-cleanup-scanning"><Loader2 className="h-4 w-4 animate-spin" /> Listando seguidos e cruzando com as compras…</p>
            )}
            {plan?.status === 'FAILED' && <p className="text-xs font-bold text-rose-600" data-testid="follow-cleanup-failed">A prévia falhou: {plan.error || 'KAKO instável'}. Tente de novo.</p>}
            {plan && c && plan.status !== 'SCANNING' && plan.status !== 'FAILED' && (
              <div className="rounded-xl border bg-slate-50 p-3" data-testid="follow-cleanup-plan">
                <div className="flex flex-wrap gap-2 text-[11px]">
                  <Badge variant="secondary">Seguidos: {c.following}</Badge>
                  <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Nunca compraram: {c.tier1}</Badge>
                  <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">+{plan.params.old_days}d: {c.tier2}</Badge>
                  <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">+{plan.params.inactive_days}d: {c.tier3}</Badge>
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Protegidos: {c.resellers + c.frequent + c.protected} (rev {c.resellers} · freq {c.frequent})</Badge>
                  <Badge variant="outline">Ativos mantidos: {c.keep_recent}</Badge>
                  <Badge className="bg-slate-800 text-white hover:bg-slate-800" data-testid="follow-cleanup-candidates">Candidatos: {c.candidates}</Badge>
                </div>

                {plan.status === 'PREVIEW' && (
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
                    <label className="text-[11px] font-bold text-slate-600">Quantidade a deixar de seguir
                      <Input type="number" min={1} max={c.candidates} value={quantity} onChange={(e) => setQuantity(e.target.value)} className="mt-1 h-8 w-32" data-testid="follow-cleanup-quantity" />
                    </label>
                    <label className="flex-1 text-[11px] font-bold text-slate-600">Digite <span className="text-rose-600">DEIXAR DE SEGUIR</span> para confirmar
                      <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 h-8" placeholder="DEIXAR DE SEGUIR" data-testid="follow-cleanup-confirm" />
                    </label>
                    <Button onClick={execute} disabled={busy || confirm.trim().toUpperCase() !== 'DEIXAR DE SEGUIR' || !c.candidates}
                      className="h-8 bg-rose-600 text-white hover:bg-rose-700" data-testid="follow-cleanup-execute">
                      <UserMinus className="mr-1 h-4 w-4" /> Deixar de seguir {Math.min(Number(quantity) || 0, c.candidates)}
                    </Button>
                  </div>
                )}

                {plan.status !== 'PREVIEW' && pr && (
                  <div className="mt-3" data-testid="follow-cleanup-progress">
                    <div className="flex justify-between text-[11px] font-bold text-slate-600">
                      <span>{plan.status === 'RUNNING' ? <><Loader2 className="mr-1 inline h-3 w-3 animate-spin" />Em andamento…</> : 'Concluído'}</span>
                      <span>{pr.done}/{pr.requested} · ✓ {pr.ok} · ✗ {pr.failed}</span>
                    </div>
                    <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-200"><div className="h-full bg-rose-500 transition-all" style={{ width: `${pct}%` }} /></div>
                  </div>
                )}

                <details className="mt-3">
                  <summary className="cursor-pointer text-[11px] font-bold text-slate-600">Prévia dos primeiros {plan.preview.length} (ordem de saída)</summary>
                  <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto text-[11px]">
                    {plan.preview.map((p) => (
                      <li key={p.kako_id} className="flex items-center justify-between rounded border bg-white px-2 py-1" data-testid="follow-cleanup-row">
                        <span className="truncate"><b>{p.nickname || '—'}</b> <span className="text-slate-400">#{p.kako_id}</span>{!p.follows_us && <span className="ml-1 text-slate-400">· não nos segue</span>}</span>
                        <span className="flex shrink-0 items-center gap-2">
                          <span className="text-slate-500">{p.orders} compra(s) · última {fmtData(p.last_purchase)}</span>
                          <span className={`rounded px-1.5 py-0.5 font-bold ${TIER[p.tier][1]}`}>{TIER[p.tier][0]}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
            )}
            {status && status.following == null && <p className="text-[11px] text-rose-600">Token de DM do KAKO indisponível — configure acima para usar a limpeza.</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FollowCleanupPanel;
